import { useConvexAuth } from "convex/react";
import { Link } from "react-router";
import { MEMBER_COLORS } from "@todotogether/shared";

// The roster teaser's three members: the first three MEMBER_COLORS.
// Colour always means a person (system.md §2.4), so the art reads its
// palette from the shared roster constants, never literals.
const ROSTER = [MEMBER_COLORS[0]!, MEMBER_COLORS[1]!, MEMBER_COLORS[2]!];

// One squiggle per row — a list being worked through. The member
// highlighter flows behind the same path (§1.3: 30% opacity, round
// caps, ≈2.5× the writing stroke).
const WRITING = [
  "M40 14 q11 -4.5 22 0 t22 0 t22 0 t22 0 t22 0 t22 0",
  "M40 42 q11 -4.5 22 0 t22 0 t22 0 t22 0",
  "M40 70 q11 -4.5 22 0 t22 0 t22 0",
];

export default function Landing() {
  // The landing is the front door: signed-out visitors get the way in,
  // signed-in ones go straight to their lists. Gate on the auth lifecycle
  // first — a query that fires before the session settles must read as
  // "loading", not "signed out" (same pattern as Settings/App). The
  // deployment seam lives on /health (#178), not here.
  const { isLoading, isAuthenticated } = useConvexAuth();
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-paper px-6 py-12">
      <div className="w-full max-w-md">
        {/* The hero card (system.md §3.2): the front door is a sheet on
            the desk — page radius, hairline, the page shadow, the sheet
            gradient re-composed from the page/pressed tokens (the §1.3
            art pair's second stop has no token; the chrome pair adapts
            to the night desk without a new spelling). */}
        <section
          className="rounded-[18px] border border-line px-6 py-10 text-center shadow-[0_5px_16px_rgba(12,22,36,0.07)] sm:px-8"
          style={{
            backgroundImage:
              "linear-gradient(180deg, var(--tt-page), var(--tt-pressed))",
          }}
        >
          {/* The roster teaser: three rows of person → writing line →
              checkbox, one axis and one member colour per row — the
              product in miniature, decorative beside the wordmark. */}
          <svg
            aria-hidden="true"
            className="mx-auto w-60 text-ink"
            viewBox="0 0 264 84"
            fill="none"
          >
            {ROSTER.map((colour, i) => {
              const y = 14 + i * 28;
              return (
                <g key={colour}>
                  <circle cx={16} cy={y - 5} r={5} fill={colour} />
                  <path d={`M8 ${y + 9} a8 8 0 0 1 16 0`} fill={colour} />
                  <path
                    d={WRITING[i]!}
                    stroke={colour}
                    strokeOpacity={0.3}
                    strokeWidth={6.5}
                    strokeLinecap="round"
                  />
                  <path
                    d={WRITING[i]!}
                    stroke="currentColor"
                    strokeWidth={2.6}
                    strokeLinecap="round"
                  />
                  {i === 0 ? (
                    <>
                      <rect
                        x={236}
                        y={y - 7}
                        width={14}
                        height={14}
                        rx={3.5}
                        fill={colour}
                      />
                      <path
                        d={`M239.5 ${y} l2.5 2.5 l5 -5.5`}
                        stroke="var(--tt-checkmark)"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  ) : (
                    <rect
                      x={236}
                      y={y - 7}
                      width={14}
                      height={14}
                      rx={3.5}
                      stroke={colour}
                      strokeWidth={2.2}
                    />
                  )}
                </g>
              );
            })}
          </svg>
          <h1 className="mt-5 font-serif text-4xl font-bold tracking-tight text-balance text-ink sm:text-5xl">
            ToDoTogether
          </h1>
          <p className="mt-4 text-lg text-pretty text-muted">
            Shared to-do lists where everyone stays in sync — fast, simple, organized.
          </p>
          {!isLoading && (
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {isAuthenticated ? (
                <Link
                  to="/app"
                  data-testid="landing-open-app"
                  className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-blue-fill px-6 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue sm:w-auto"
                >
                  Open your lists
                </Link>
              ) : (
                <>
                  <Link
                    to="/signin"
                    data-testid="landing-signin"
                    className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-blue-fill px-6 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue sm:w-auto"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    data-testid="landing-signup"
                    className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-line bg-page px-6 py-2 text-sm font-medium text-ink shadow-sm hover:bg-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue sm:w-auto"
                  >
                    Create an account
                  </Link>
                </>
              )}
            </div>
          )}
        </section>
        <footer className="mt-8 flex items-center justify-center gap-4 text-sm text-muted">
          <Link
            to="/privacy"
            className="hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            Privacy policy
          </Link>
          <span aria-hidden>·</span>
          <Link
            to="/support"
            className="hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            Support
          </Link>
        </footer>
      </div>
    </main>
  );
}
